import React, { useState, useMemo } from 'react';
import Dropzone from 'react-dropzone';
import { mobileModel } from 'react-device-detect';

/**
 * FileField — Survey Form V2
 *
 * Soporta single (legacy) y multi-file.
 *
 * Props:
 *   question        — objeto pregunta (id, is_mandatory, is_active, is_enable, special_value, is_multiple)
 *   files           — array de objetos archivo: [{ id, name, url, size }, ...]
 *                     Single legacy puede llegar como [stringURL] o [{name,url}] — normalizeFile resuelve.
 *   isMultiple      — bool
 *   surveyState     — disabled cuando saved
 *   uploadProgress  — { phase, progress, message } o null
 *   isCompressing   — bool
 *   error           — { question_id, message } o null
 *   onDrop          — (files, question.id, is_mandatory, is_active, question) => void
 *   onDelete        — (question, fileId|null) => void  (fileId requerido en multi)
 */

const IMAGE_EXT = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'svg', 'bmp', 'heic', 'heif'];
const VIDEO_EXT = ['mp4', 'webm', 'mov', 'avi', 'mkv', 'm4v'];
const AUDIO_EXT = ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac'];
const PDF_EXT = ['pdf'];
const DOC_EXT = ['doc', 'docx', 'odt', 'rtf', 'txt'];
const SHEET_EXT = ['xls', 'xlsx', 'csv', 'ods'];
const SLIDE_EXT = ['ppt', 'pptx', 'odp'];
const ARCHIVE_EXT = ['zip', 'rar', '7z', 'tar', 'gz'];

function getExt(name) {
    if (!name) return '';
    const i = name.lastIndexOf('.');
    return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
}

function getFileKind(file) {
    if (!file || !file.name) return 'unknown';
    const ext = getExt(file.name);
    if (IMAGE_EXT.includes(ext)) return 'image';
    if (VIDEO_EXT.includes(ext)) return 'video';
    if (AUDIO_EXT.includes(ext)) return 'audio';
    if (PDF_EXT.includes(ext)) return 'pdf';
    if (DOC_EXT.includes(ext)) return 'doc';
    if (SHEET_EXT.includes(ext)) return 'sheet';
    if (SLIDE_EXT.includes(ext)) return 'slide';
    if (ARCHIVE_EXT.includes(ext)) return 'archive';
    return 'other';
}

function getKindIcon(kind) {
    switch (kind) {
        case 'image': return 'fas fa-image';
        case 'video': return 'fas fa-video';
        case 'audio': return 'fas fa-music';
        case 'pdf':   return 'fas fa-file-pdf';
        case 'doc':   return 'fas fa-file-word';
        case 'sheet': return 'fas fa-file-excel';
        case 'slide': return 'fas fa-file-powerpoint';
        case 'archive': return 'fas fa-file-archive';
        default: return 'fas fa-file';
    }
}

function getKindLabel(kind) {
    switch (kind) {
        case 'image': return 'Imagen';
        case 'video': return 'Video';
        case 'audio': return 'Audio';
        case 'pdf':   return 'Documento PDF';
        case 'doc':   return 'Documento';
        case 'sheet': return 'Hoja de cálculo';
        case 'slide': return 'Presentación';
        case 'archive': return 'Archivo comprimido';
        default: return 'Archivo';
    }
}

function formatBytes(bytes) {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / 1024 / 1024).toFixed(2) + ' MB';
    return (bytes / 1024 / 1024 / 1024).toFixed(2) + ' GB';
}

function isUrlLike(str) {
    return typeof str === 'string' && /^https?:\/\//i.test(str);
}

function stripUuidPrefix(name) {
    if (!name) return name;
    const fullUuidMatch = name.match(/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}(\.[^.]+)?$/i);
    if (fullUuidMatch) return `archivo${fullUuidMatch[1] || ''}`;
    return name.replace(/^[a-f0-9]{8}_/i, '');
}

function extractFilenameFromUrl(url) {
    if (!url) return '';
    try {
        const u = new URL(url);
        const segs = u.pathname.split('/').filter(Boolean);
        return stripUuidPrefix(decodeURIComponent(segs[segs.length - 1] || ''));
    } catch (e) {
        const cleaned = String(url).split('?')[0];
        const segs = cleaned.split('/').filter(Boolean);
        try { return stripUuidPrefix(decodeURIComponent(segs[segs.length - 1] || '')); }
        catch (_e) { return stripUuidPrefix(segs[segs.length - 1] || ''); }
    }
}

function normalizeFile(raw) {
    if (!raw) return null;
    if (typeof raw === 'string') {
        if (!isUrlLike(raw)) return null;
        return { id: undefined, name: extractFilenameFromUrl(raw), url: raw, size: undefined };
    }
    if (typeof raw === 'object') {
        const id = raw.id;
        const name = raw.name;
        const url = raw.url || (isUrlLike(name) ? name : undefined);
        if (!url && !name) return null;
        if (isUrlLike(name)) {
            return { id, name: extractFilenameFromUrl(name), url: url || name, size: raw.size };
        }
        // Si name es UUID-prefijado, limpiar
        return { id, name: name ? stripUuidPrefix(name) : (url ? extractFilenameFromUrl(url) : ''), url, size: raw.size };
    }
    return null;
}

// =====================================================================
// Sub-componente: tarjeta de archivo individual con lightbox
// =====================================================================
function FilePreview({ file, surveyState, onDelete, onReplace }) {
    const [showLightbox, setShowLightbox] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const kind = useMemo(() => getFileKind(file), [file]);

    const handleDelete = () => {
        setConfirmingDelete(false);
        onDelete && onDelete(file);
    };

    return (
        <div className={`bw-file-field__preview bw-file-field__preview--${kind}`}>
            <div
                className="bw-file-field__thumb"
                onClick={() => (kind === 'image' || kind === 'video' || kind === 'pdf') && setShowLightbox(true)}
                role={kind === 'image' || kind === 'video' || kind === 'pdf' ? 'button' : undefined}
            >
                {kind === 'image' ? (
                    <img src={file.url} alt={file.name} />
                ) : kind === 'video' ? (
                    <video src={file.url} preload="metadata" muted />
                ) : (
                    <div className={`bw-file-field__icon bw-file-field__icon--${kind}`}>
                        <i className={getKindIcon(kind)} />
                    </div>
                )}
                {(kind === 'image' || kind === 'video' || kind === 'pdf') && (
                    <div className="bw-file-field__thumb-overlay"><i className="fas fa-expand" /></div>
                )}
            </div>

            <div className="bw-file-field__meta">
                <div className="bw-file-field__meta-kind">{getKindLabel(kind)}</div>
                <a
                    className="bw-file-field__meta-name"
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={file.name}
                >
                    {file.name}
                </a>
                {file.size != null && (
                    <div className="bw-file-field__meta-size">{formatBytes(file.size)}</div>
                )}
            </div>

            <div className="bw-file-field__actions">
                {!surveyState && !confirmingDelete && onReplace && (
                    <Dropzone onDrop={onReplace} multiple={false}>
                        {({ getRootProps, getInputProps }) => (
                            <div {...getRootProps({ className: 'bw-file-field__action bw-file-field__action--ghost', title: 'Reemplazar archivo' })}>
                                <input {...getInputProps()} />
                                <i className="fas fa-sync-alt" />
                                <span>Reemplazar</span>
                            </div>
                        )}
                    </Dropzone>
                )}

                <a
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bw-file-field__action bw-file-field__action--ghost bw-file-field__action--icon-only"
                    title="Abrir en nueva pestaña"
                >
                    <i className="fas fa-external-link-alt" />
                </a>

                {!surveyState && (
                    confirmingDelete ? (
                        <div className="bw-file-field__confirm">
                            <button
                                type="button"
                                className="bw-file-field__action bw-file-field__action--danger"
                                onClick={handleDelete}
                                title="Confirmar eliminación"
                            >
                                <i className="fas fa-check" /> <span>Eliminar</span>
                            </button>
                            <button
                                type="button"
                                className="bw-file-field__action bw-file-field__action--ghost bw-file-field__action--icon-only"
                                onClick={() => setConfirmingDelete(false)}
                                title="Cancelar"
                            >
                                <i className="fas fa-times" />
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            className="bw-file-field__action bw-file-field__action--danger-ghost bw-file-field__action--icon-only"
                            onClick={() => setConfirmingDelete(true)}
                            title="Eliminar"
                        >
                            <i className="fas fa-trash" />
                        </button>
                    )
                )}
            </div>

            {showLightbox && (
                <div className="bw-file-field__lightbox" onClick={() => setShowLightbox(false)} role="dialog">
                    <button
                        type="button"
                        className="bw-file-field__lightbox-close"
                        onClick={() => setShowLightbox(false)}
                        aria-label="Cerrar"
                    >
                        <i className="fas fa-times" />
                    </button>
                    <div className="bw-file-field__lightbox-content" onClick={(e) => e.stopPropagation()}>
                        {kind === 'image' && <img src={file.url} alt={file.name} />}
                        {kind === 'video' && <video src={file.url} controls autoPlay />}
                        {kind === 'pdf' && <iframe src={file.url} title={file.name} className="bw-file-field__lightbox-pdf" />}
                    </div>
                    <div className="bw-file-field__lightbox-name">{file.name}</div>
                </div>
            )}
        </div>
    );
}

// =====================================================================
// Sub-componente: Dropzone vacío
// =====================================================================
function EmptyDropzone({ question, isDragOver, surveyState, isMultiple, onDrop, onDragEnter, onDragLeave }) {
    return (
        <Dropzone
            onDrop={onDrop}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            disabled={surveyState}
            multiple={!!isMultiple}
        >
            {({ getRootProps, getInputProps }) => (
                <div {...getRootProps({ className: `bw-file-field__dropzone ${isDragOver ? 'is-dragover' : ''} ${surveyState ? 'is-disabled' : ''}` })}>
                    {question.special_value === 'camera'
                        ? <input capture="user" {...getInputProps()} />
                        : <input {...getInputProps()} />}

                    <div className="bw-file-field__dropzone-icon">
                        <i className={isDragOver ? 'fas fa-arrow-down' : 'fas fa-arrow-up'} />
                    </div>

                    <div className="bw-file-field__dropzone-body">
                        <div className="bw-file-field__dropzone-title">
                            {isDragOver
                                ? 'Suelta para subir'
                                : (isMultiple ? 'Arrastra archivos aquí' : 'Arrastra el archivo aquí')}
                        </div>
                        <div className="bw-file-field__dropzone-hint">
                            o <span className="bw-file-field__dropzone-browse">haz clic para seleccionar</span>
                        </div>
                    </div>

                    <div className="bw-file-field__dropzone-meta">
                        <div className="bw-file-field__dropzone-meta-line">
                            {isMultiple ? 'Varios archivos' : 'Cualquier formato'}
                        </div>
                        <div className="bw-file-field__dropzone-meta-line bw-file-field__dropzone-meta-line--soft">hasta 100 MB</div>
                    </div>

                    {question.special_value === 'camera' && (
                        <div className="bw-file-field__dropzone-camera-hint">
                            <i className="fas fa-camera mr-1" /> Cámara
                        </div>
                    )}
                </div>
            )}
        </Dropzone>
    );
}

// =====================================================================
// Sub-componente: Indicador de upload en progreso
// =====================================================================
function UploadingIndicator({ uploadProgress }) {
    return (
        <div className="bw-file-field__uploading">
            <div className="bw-file-field__uploading-header">
                <div className="bw-file-field__uploading-spinner">
                    <i className="fas fa-spinner fa-spin" />
                </div>
                <div className="bw-file-field__uploading-text">
                    <strong>{(uploadProgress && uploadProgress.message) || 'Procesando archivo...'}</strong>
                    {uploadProgress && typeof uploadProgress.progress === 'number' && (
                        <span className="bw-file-field__uploading-percent">{Math.round(uploadProgress.progress)}%</span>
                    )}
                </div>
            </div>
            <div className="bw-file-field__progress">
                <div
                    className="bw-file-field__progress-bar"
                    style={{
                        width: uploadProgress && typeof uploadProgress.progress === 'number'
                            ? `${Math.max(2, Math.min(100, uploadProgress.progress))}%` : '20%',
                        transition: 'width 200ms ease',
                    }}
                />
            </div>
        </div>
    );
}

// =====================================================================
// Componente principal
// =====================================================================
export default function FileField({
    question,
    files,
    isMultiple,
    surveyState,
    uploadProgress,
    isCompressing,
    error,
    onDrop,
    onDelete,
}) {
    const [isDragOver, setIsDragOver] = useState(false);

    // Normalizar array (puede venir mixto: strings o objects)
    const normalizedFiles = useMemo(
        () => (Array.isArray(files) ? files.map(normalizeFile).filter(Boolean) : []),
        [files]
    );
    const hasFiles = normalizedFiles.length > 0;
    const isUploading = !!isCompressing;
    const hasError = error && error.question_id === question.id && error.message;

    if (!question.is_enable) {
        return (
            <div className="bw-file-field bw-file-field--inactive">
                <i className="fas fa-ban mr-2" />
                <span>Pregunta inactiva</span>
            </div>
        );
    }

    const handleDrop = async (acceptedFiles) => {
        setIsDragOver(false);
        if (!acceptedFiles || acceptedFiles.length === 0) return;

        if (isMultiple && acceptedFiles.length > 1) {
            // Multi: subir uno por uno secuencialmente (evita race conditions de setState)
            for (const f of acceptedFiles) {
                await onDrop([f], question.id, question.is_mandatory, question.is_active, question);
            }
        } else {
            // Single o multi con 1 solo archivo: flujo normal
            onDrop(acceptedFiles, question.id, question.is_mandatory, question.is_active, question);
        }
    };

    const handleDeleteFile = (file) => {
        onDelete && onDelete(question, file.id || null);
    };

    // En single + reemplazo: borrar primero y subir
    const handleReplaceSingle = (acceptedFiles) => {
        if (acceptedFiles && acceptedFiles.length > 0) {
            onDrop(acceptedFiles, question.id, question.is_mandatory, question.is_active, question);
        }
    };

    return (
        <div className={`bw-file-field ${isMultiple ? 'bw-file-field--multi' : ''}`}>
            {/* Gallery de archivos */}
            {hasFiles && (
                <div className={`bw-file-field__gallery ${isMultiple ? 'bw-file-field__gallery--grid' : ''}`}>
                    {normalizedFiles.map((file, idx) => (
                        <FilePreview
                            key={file.id || idx}
                            file={file}
                            surveyState={surveyState}
                            onDelete={handleDeleteFile}
                            onReplace={!isMultiple ? handleReplaceSingle : null}
                        />
                    ))}
                </div>
            )}

            {/* Dropzone para agregar archivos:
                - Single sin archivo → mostrar dropzone
                - Multi → siempre mostrar dropzone "Agregar archivo" debajo del gallery
            */}
            {((!hasFiles && !isUploading) || (isMultiple && !isUploading)) && (
                <EmptyDropzone
                    question={question}
                    isDragOver={isDragOver}
                    surveyState={surveyState}
                    isMultiple={isMultiple}
                    onDrop={handleDrop}
                    onDragEnter={() => setIsDragOver(true)}
                    onDragLeave={() => setIsDragOver(false)}
                />
            )}

            {/* Indicador de upload */}
            {isUploading && <UploadingIndicator uploadProgress={uploadProgress} />}

            {/* iPhone hint */}
            {mobileModel === 'iPhone' && (
                <div className="bw-file-field__hint bw-file-field__hint--info">
                    <i className="fas fa-info-circle mr-1" />
                    Si subes una imagen desde iPhone, debe tomarse en orientación horizontal
                </div>
            )}

            {/* Error */}
            {hasError && (
                <div className="bw-file-field__hint bw-file-field__hint--error">
                    <i className="fas fa-exclamation-triangle mr-1" />
                    {error.message}
                </div>
            )}
        </div>
    );
}
